/**
 * EN STILLE LINJE, IKKE ET VINDU (B3, 253.26).
 *
 * Til bølge B var dette «Velkommen til Tvitvi», et vindu som ikke kunne lukkes før telefon og språk
 * var fylt ut (QA-runde 12, #7). Nå spør invitasjonssiden og «Opprett organisasjonen» om de samme
 * feltene i ett steg, og den som har konto fra før får ikke noe skjema. Det som er igjen er de som
 * mangler telefon av andre grunner — brukere fra før regelen, og innlogging med Google eller Microsoft
 * senere. Telefonen hindrer ingenting i appen, så den stopper ingen: en linje øverst, med «Legg det
 * inn», og «Ikke nå» som skjuler den i økta.
 *
 * SERVEREN BESTEMMER AT DEN STÅR HER. `needsOnboarding` regnes ett sted — `UsersService.profile()`.
 */
import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { myProfileQueryOptions } from '@/api/queries/my-profile';
import { Button } from '@/components/ui/Button';
import { MyProfileDialog } from '@/features/settings/MyProfileDialog';

export function OnboardingGate({ organizationId }: { organizationId: string }): ReactNode {
  const { t } = useTranslation();
  const profile = useQuery(myProfileQueryOptions());
  const [editing, setEditing] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (profile.data === undefined || !profile.data.needsOnboarding) return null;

  return (
    <>
      {dismissed ? null : (
        <div
          role="status"
          className="mx-[var(--shell-gap)] mt-[var(--shell-gap)] flex flex-wrap items-center gap-x-3 gap-y-1 rounded-[var(--radius-inner)] bg-[var(--fill-tonal)] px-3 py-1.5 text-sm text-default"
          data-testid="onboarding-line"
        >
          <span className="min-w-0 flex-1">{t('profile.missingPhone.line')}</span>
          <Button
            size="sm"
            variant="tertiary"
            onClick={() => setEditing(true)}
            data-testid="onboarding-line-open"
          >
            {t('profile.missingPhone.action')}
          </Button>
          <Button
            size="sm"
            variant="quiet"
            onClick={() => setDismissed(true)}
            data-testid="onboarding-line-dismiss"
          >
            {t('profile.missingPhone.notNow')}
          </Button>
        </div>
      )}
      {editing ? (
        <MyProfileDialog
          open
          onOpenChange={(next) => {
            if (!next) setEditing(false);
          }}
          organizationId={organizationId}
          mode="profile"
        />
      ) : null}
    </>
  );
}
